
import {
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  computed,
  onActivated,
  onDeactivated,
} from 'vue';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';

dayjs.extend(customParseFormat);

export default defineComponent({
  name: 'SelecionarDataInicialFinal',
  props: {
    dataInicialSelecionada: {
      type: String,
      default: '',
    },
    dataFinalSelecionada: {
      type: String,
      default: '',
    },
    horaMinuto: {
      type: Boolean,
      default: false,
    },
    horaMinutoSegundo: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: Array as () => string[],
      default: () => ['Data Inicial', 'Data Final'],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    classCss: {
      type: String,
      default: '',
    },
  },
  emits: ['update:dataInicialSelecionada', 'update:dataFinalSelecionada', 'update:horaMinutoSegundo', 'change'],
  setup(props, { emit }) {
    const rangePickerRef = ref(null);
    let inputInicio: HTMLInputElement | null = null;
    let inputFim: HTMLInputElement | null = null;

    function obterFormatoData() {
      if (props.horaMinutoSegundo) {
        return UtilitarioData.formatoDataDiaMesAnoHoraMinutoSegundo;
      } if (props.horaMinuto) {
        return UtilitarioData.formatoDataDiaMesAnoHoraMinuto;
      }
      return UtilitarioData.formatoDataDiaMesAno;
    }

    const datasSelecionadas = computed<[Dayjs | null, Dayjs | null]>({
      get() {
        const dataInicial = UtilitarioGeral.valorValido(props.dataInicialSelecionada)
          ? UtilitarioData.converterDataParaDayJs(props.dataInicialSelecionada)
          : null;
        const dataFinal = UtilitarioGeral.valorValido(props.dataFinalSelecionada)
          ? UtilitarioData.converterDataParaDayJs(props.dataFinalSelecionada)
          : null;
        return [dataInicial, dataFinal];
      },
      set(novasDatas) {
        const [novaDataInicial, novaDataFinal] = novasDatas || [null, null];
        const formato = props.horaMinuto || props.horaMinutoSegundo ? UtilitarioData.converterDataHoraMinutoSegundoJson : UtilitarioData.converterDataJson;

        emit('update:dataInicialSelecionada', novaDataInicial ? formato(novaDataInicial) : '');
        emit('update:dataFinalSelecionada', novaDataFinal ? formato(novaDataFinal) : '');
      },
    });

    function change() {
      emit('change', props.dataInicialSelecionada, props.dataFinalSelecionada);
    }

    function formatarData(event: Event, tipo: 'inicio' | 'fim') {
      const input = event.target as HTMLInputElement;
      const valorInput = input.value;
      console.log(`[formatarData] Acionado para campo '${tipo}' com valor: '${valorInput}'`);

      if (!valorInput) {
        return;
      }

      let novaData: Dayjs | null = null;
      const hoje = dayjs();

      if (valorInput.startsWith('+') || valorInput.startsWith('-')) {
        const dias = parseInt(valorInput, 10);
        if (!Number.isNaN(dias)) {
          novaData = hoje.add(dias, 'day');
        }
      } else {
        const soNumeros = valorInput.replace(/\D/g, '');
        if (soNumeros) {
          if (soNumeros.length === 4) { // DDMM
            novaData = dayjs(soNumeros, 'DDMM');
          } else if (soNumeros.length === 6) { // DDMMYY
            novaData = dayjs(soNumeros, 'DDMMYY');
          } else if (soNumeros.length >= 8) { // DDMMYYYY
            novaData = dayjs(soNumeros.substring(0, 8), 'DDMMYYYY');
          }
        }
      }

      if (novaData && novaData.isValid()) {
        console.log(`[formatarData] Data válida encontrada: ${novaData.format()}`);
        const [dataInicialAtual, dataFinalAtual] = datasSelecionadas.value;
        if (tipo === 'inicio') {
          datasSelecionadas.value = [novaData, dataFinalAtual];
        } else {
          datasSelecionadas.value = [dataInicialAtual, novaData];
        }
        if (rangePickerRef.value) {
          (rangePickerRef.value as any).blur();
        }
      } else {
        console.log(`[formatarData] Nenhum formato de data válido encontrado para: '${valorInput}'`);
      }
    }

    function handleKeydown(event: KeyboardEvent, tipo: 'inicio' | 'fim') {
      console.log(`[handleKeydown] Tecla pressionada: '${event.key}' no campo '${tipo}'`);
      const input = event.target as HTMLInputElement;
      const key = event.key.toLowerCase();
      let dataParaSetar: Dayjs | null = null;
      const hoje = dayjs();

      if (input.value === '') {
        if (key === 'h') {
          dataParaSetar = hoje;
        } else if (key === 'o') {
          dataParaSetar = hoje.subtract(1, 'day');
        } else if (key === 'a') {
          dataParaSetar = hoje.add(1, 'day');
        }
      }

      if (dataParaSetar) {
        event.preventDefault();
        event.stopPropagation();
        console.log(`[handleKeydown] Atalho detectado. Setando data para: ${dataParaSetar.format()}`);

        const [dataInicialAtual, dataFinalAtual] = datasSelecionadas.value;
        if (tipo === 'inicio') {
          datasSelecionadas.value = [dataParaSetar, dataFinalAtual];
        } else {
          datasSelecionadas.value = [dataInicialAtual, dataParaSetar];
        }

        if (rangePickerRef.value) {
          (rangePickerRef.value as any).blur();
        }
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        formatarData(event, tipo);
      }
    }

    function handleBlur(event: FocusEvent, tipo: 'inicio' | 'fim') {
      console.log(`[handleBlur] Campo '${tipo}' perdeu o foco.`);
      formatarData(event, tipo);
    }

    function handleInput(event: Event, tipo: 'inicio' | 'fim') {
      if ((event.target as HTMLInputElement).value === '') {
        console.log(`[handleInput] Campo '${tipo}' limpo.`);
        const [dataInicialAtual, dataFinalAtual] = datasSelecionadas.value;
        if (tipo === 'inicio') {
          datasSelecionadas.value = [null, dataFinalAtual];
        } else {
          datasSelecionadas.value = [dataInicialAtual, null];
        }
      }
    }

    const handleInputInicio = (e: Event) => handleInput(e, 'inicio');
    const handleKeydownInicio = (e: KeyboardEvent) => handleKeydown(e, 'inicio');
    const handleBlurInicio = (e: FocusEvent) => handleBlur(e, 'inicio');

    const handleInputFim = (e: Event) => handleInput(e, 'fim');
    const handleKeydownFim = (e: KeyboardEvent) => handleKeydown(e, 'fim');
    const handleBlurFim = (e: FocusEvent) => handleBlur(e, 'fim');

    const attachListeners = () => {
      console.log('--- attachListeners: Iniciando busca por inputs ---');
      let retries = 0;
      const maxRetries = 100;

      function findInputsAndAttachListeners() {
        retries += 1;
        const picker = rangePickerRef.value as any;

        if (picker && picker.$el) {
          const inputs = picker.$el.querySelectorAll('input');
          if (inputs.length === 2) {
            console.log(`[attachListeners] Inputs encontrados na tentativa #${retries}! Anexando listeners.`);
            [inputInicio, inputFim] = inputs;
            if (inputInicio) {
              inputInicio.addEventListener('input', handleInputInicio);
              inputInicio.addEventListener('keydown', handleKeydownInicio);
              inputInicio.addEventListener('blur', handleBlurInicio);
            }
            if (inputFim) {
              inputFim.addEventListener('input', handleInputFim);
              inputFim.addEventListener('keydown', handleKeydownFim);
              inputFim.addEventListener('blur', handleBlurFim);
            }
            return;
          }
        }

        if (retries < maxRetries) {
          setTimeout(findInputsAndAttachListeners, 20);
        } else {
          console.error(`[attachListeners] Não foi possível encontrar os inputs após ${maxRetries} tentativas.`);
        }
      }

      findInputsAndAttachListeners();
    };

    const removeListeners = () => {
      console.log('--- removeListeners: Removendo listeners ---');
      if (inputInicio) {
        inputInicio.removeEventListener('input', handleInputInicio);
        inputInicio.removeEventListener('keydown', handleKeydownInicio);
        inputInicio.removeEventListener('blur', handleBlurInicio);
      }
      if (inputFim) {
        inputFim.removeEventListener('input', handleInputFim);
        inputFim.removeEventListener('keydown', handleKeydownFim);
        inputFim.removeEventListener('blur', handleBlurFim);
      }
    };

    onMounted(() => {
      console.log('--- onMounted: Hook disparado ---');
      attachListeners();
    });

    onUnmounted(() => {
      console.log('--- onUnmounted: Hook disparado ---');
      removeListeners();
    });

    onActivated(() => {
      console.log('--- onActivated: Hook disparado ---');
      attachListeners();
    });

    onDeactivated(() => {
      console.log('--- onDeactivated: Hook disparado ---');
      removeListeners();
    });

    return {
      props,
      rangePickerRef,
      datasSelecionadas,
      change,
      obterFormatoData,
    };
  },
});
